/** Design Imports → Proposals: every Composition Proposal, newest first —
 * the persistent answer to "my import finished, where did it go?". A row
 * opens the review page; nothing here mutates. */
import { Box, Chip, MenuItem, TextField } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import * as proposalsApi from '@/api/compositionProposals';
import type { ProposalListItem, ProposalStatus } from '@/api/compositionProposals';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import {
  ResourceDataGrid,
  type ResourceDataGridParams,
} from '@/components/DataGrid/ResourceDataGrid';
import { ResponsiveFilterBar, type FilterBarItem } from '@/components/DataGrid/ResponsiveFilterBar';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { operatorActions } from '@/components/ValueActions/identity';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { designImportsHref, userActions } from '@/features/notifications/notificationLinks';
import { formatTenantIds } from '@/scope/tenantIds';
import { useScopedTenantsState } from '@/scope/useScopedTenantsState';

const STATUS_COLOR: Record<string, 'default' | 'info' | 'warning' | 'success' | 'error'> = {
  draft: 'default',
  review: 'info',
  applying: 'warning',
  applied: 'success',
  rejected: 'default',
  failed: 'error',
};

const STATUS_OPTIONS: ProposalStatus[] = ['review', 'applying', 'applied', 'rejected', 'failed'];

export function ProposalsPanel() {
  const navigate = useNavigate();
  // The operators (several, D1); they follow the console scope until picked here.
  const [tenants, setTenants] = useScopedTenantsState();
  const tenantIds = formatTenantIds(tenants.map((t) => t.id));
  const [status, setStatus] = useState<'all' | ProposalStatus>('all');

  const columns = useMemo<GridColDef<ProposalListItem>[]>(
    () => [
      copyableColumn({ field: 'id', headerName: 'ID', width: 80 }),
      {
        field: 'tenant',
        headerName: 'Operator',
        width: 150,
        valueGetter: (_, row) => row.tour_operator.code || row.tour_operator.name,
        renderCell: (p) => (
          // The row click opens the proposal, so the operator's own links live
          // in the cell's popover.
          <WithTenantMark environment={p.row.tour_operator.environment}>
            <ValueActions
              actions={operatorActions(p.row.tour_operator, {
                href: designImportsHref({ tenant: p.row.tour_operator.id }),
                label: 'Design imports for this operator',
              })}
            >
              {p.row.tour_operator.code || p.row.tour_operator.name}
            </ValueActions>
          </WithTenantMark>
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 110,
        renderCell: (p) => (
          <Chip size="small" color={STATUS_COLOR[p.row.status] ?? 'default'} label={p.row.status} />
        ),
      },
      { field: 'origin', headerName: 'Origin', width: 90 },
      {
        field: 'item_count',
        headerName: 'Items',
        width: 80,
        sortable: false,
        valueGetter: (_, row) => row.item_count ?? '',
      },
      {
        field: 'open_blocking',
        headerName: 'Blocking open',
        width: 120,
        sortable: false,
        valueGetter: (_, row) =>
          typeof row.open_blocking_questions === 'number' ? row.open_blocking_questions : '',
        renderCell: (p) => {
          const open = p.row.open_blocking_questions;
          if (typeof open !== 'number' || open === 0) return '—';
          return <Chip size="small" color="warning" variant="outlined" label={open} />;
        },
      },
      {
        field: 'created_by',
        headerName: 'Started by',
        width: 200,
        sortable: false,
        valueGetter: (_, row) => row.created_by?.email ?? '',
        renderCell: (p) =>
          p.row.created_by ? (
            <ValueActions actions={userActions(p.row.created_by)}>
              {p.row.created_by.email}
            </ValueActions>
          ) : (
            '—'
          ),
      },
      {
        field: 'created_ts',
        headerName: 'Created',
        width: 170,
        valueGetter: (_, row) => (row.created_ts ? new Date(row.created_ts).toLocaleString() : ''),
      },
    ],
    [],
  );

  const queryFn = useCallback(
    (params: ResourceDataGridParams) =>
      proposalsApi.list({
        page: params.page,
        pageSize: params.pageSize,
        ordering: params.ordering ?? '-created_ts',
        ...(tenantIds ? { tour_operator_id: tenantIds } : {}),
        ...(status !== 'all' ? { status } : {}),
      }),
    [tenantIds, status],
  );

  const toolbarItems: FilterBarItem[] = [
    {
      key: 'tenant',
      priority: 5,
      active: tenants.length > 0,
      node: (
        <Box sx={{ minWidth: 280 }}>
          <TenantPicker multiple value={tenants} onChange={setTenants} />
        </Box>
      ),
    },
    {
      key: 'status',
      priority: 4,
      active: status !== 'all',
      node: (
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          sx={{ width: 150 }}
          onChange={(e) => setStatus(e.target.value as 'all' | ProposalStatus)}
        >
          <MenuItem value="all">Any status</MenuItem>
          {STATUS_OPTIONS.map((s) => (
            <MenuItem key={s} value={s}>
              {s}
            </MenuItem>
          ))}
        </TextField>
      ),
    },
  ];

  return (
    <>
      <ResponsiveFilterBar items={toolbarItems} />
      <ResourceDataGrid<ProposalListItem>
        queryKey={['composition-proposals', tenantIds, status]}
        queryFn={queryFn}
        columns={columns}
        defaultSort={{ field: 'created_ts', sort: 'desc' }}
        onRowClick={(id) => navigate(`/notifications/design-imports/proposals/${id}`)}
        emptyMessage="No proposals yet — run an import from the Materials tab."
      />
    </>
  );
}
